import {
  isMultileg,
  ProductTypeEnum,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useUnifiedLiquidityContext,
  type MarketDataCardConfig,
} from '@talos/kyoko';
import { v1 as uuid } from 'uuid';
import { useMarketDataCardSymbolChangeSelections } from './useMarketDataCardSymbolChangeSelections';

interface UseMarketDataCardHeaderSymbolChangeParams {
  config: MarketDataCardConfig;
  onSaveCard: (args: MarketDataCardConfig) => void;
  onOpenMarketSelectorFromHeaderButton: () => void;
}

export function useMarketDataCardHeaderSymbolChange({
  config,
  onSaveCard,
  onOpenMarketSelectorFromHeaderButton,
}: UseMarketDataCardHeaderSymbolChangeParams) {
  const { symbol, unifiedLiquidity } = config;
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const getSymbolChangeSelections = useMarketDataCardSymbolChangeSelections();

  return useCallbackRef((nextSymbol?: string) => {
    if (!nextSymbol || nextSymbol === symbol) {
      return;
    }

    const nextUnifiedLiquidity =
      unifiedLiquidity === UnifiedLiquidityEnum.Enabled && unifiedLiquidityTokenBySymbol?.has(nextSymbol)
        ? UnifiedLiquidityEnum.Enabled
        : UnifiedLiquidityEnum.Disabled;
    const selections = getSymbolChangeSelections(nextSymbol, nextUnifiedLiquidity === UnifiedLiquidityEnum.Enabled);
    if (!selections) {
      return;
    }
    const nextSecurity = selections.security;

    onSaveCard({
      ...config,
      id: config.id || uuid(),
      symbol: nextSecurity.Symbol,
      priceIncrement: nextSecurity.DefaultPriceIncrement,
      sizeBuckets: nextSecurity.SizeBuckets,
      topOfBookSizeBucket: nextSecurity.NormalSize,
      orderPresetID: undefined,
      selectedAggregation: selections.selectedAggregation,
      selectedMarkets: selections.selectedMarkets,
      availableMarkets: selections.selectedMarkets,
      selectedMarketAccounts: selections.selectedMarketAccounts,
      availableMarketAccounts: selections.selectedMarketAccounts,
      detailsExpanded: nextSecurity.ProductType === ProductTypeEnum.Spot ? false : config.detailsExpanded,
      unifiedLiquidity: nextUnifiedLiquidity,
    });

    if (!isMultileg(nextSecurity)) {
      onOpenMarketSelectorFromHeaderButton();
    }
  });
}
